/*
 This file is part of GNU Taler
 (C) 2025 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */
import { CancellationToken, TalerError } from "@gnu-taler/taler-util";
import { error } from "console";
import { useCallback, useEffect, useRef, useState } from "preact/hooks";

/**
 * convert the async function into preact hook
 *
 * @param callback the async function
 *
 * @returns operation status
 */
export function useAsync<Res>(
  callback: (() => Promise<Res>) | undefined,
  deps: Array<any> = [],
) {
  const [data, setData] = useState<Res>();
  const [error, setError] = useState<TalerError>();

  useEffect(() => {
    let unloaded = false;
    if (callback) {
      callback()
        .then((resp) => {
          if (unloaded) return;
          setData(resp);
        })
        .catch((error: unknown) => {
          if (unloaded) return;
          if (error instanceof TalerError) {
            setError(error);
          } else {
            setError(TalerError.fromException(error));
          }
        });
    }
    return () => {
      unloaded = true;
    };
  }, deps);

  if (error) return error;
  if (!data) return undefined;
  return data;
}

export const LONG_POLL_DELAY = 15000;

// FIXME: the problem with this compared with useSWR is that
// if the hook is called from more than one place then
// you will have multiple request. This needs to be merged, maybe based on a
// key
/**
 * First start with `initial` value, if initial is undefined then finish.
 * Otherwise:
 * Based on `initial` check if it should do long-polling with `shouldRetryFn`
 * If the result is undefined then finish.
 * Otherwise:
 * Verify if the call is going to fast, if so slow down.
 *
 * Call `retryFn` as the long poll function.
 * The result will be the next `initial` value.
 *
 *
 *
 * @param initial what we already know about the state
 * @param shouldRetryFn verify if we need to do long poll based on what we know
 * @param retryFn the long polling function that should return the same type of initial value
 * @param deps
 * @returns
 */
export function useLongPolling<Res>(
  initial: Res,
  shouldRetryFn: (res: Res) => boolean,
  retryFn: (ct: CancellationToken, last: Res, deps: Array<any>) => Promise<Res>,
  deps: Array<any> = [],
  opts: { minTime?: number } = {},
) {
  const minTime = opts?.minTime ?? 1000;

  const [result, setResult] = useState(initial);

  useEffect(() => {
    setResult(initial);
  }, [initial, ...deps]);

  const ct = useRef<{
    ct: CancellationToken.Source | undefined;
    unloaded: boolean;
    startMs: number;
  }>({ ct: undefined, unloaded: false, startMs: 0 });

  useEffect(() => {
    // if (result === undefined || result instanceof TalerError) return;

    const tk = CancellationToken.create();
    ct.current.ct = tk;

    const doWeRetry = shouldRetryFn(result);
    if (!doWeRetry) return;

    const diff = new Date().getTime() - ct.current.startMs;
    if (ct.current.startMs === 0 || diff > minTime) {
      ct.current.startMs = new Date().getTime();
      retryFn(tk.token, result, deps)
        .then((r) => {
          if (!tk.token.isCancelled) {
            setResult(r);
          }
        })
        .catch((error) => console.log(""));
    } else {
      // calling too fast, wait whats left to reach minTime
      delayMs(minTime - diff).then(() => {
        if (ct.current.unloaded) return;
        ct.current.startMs = new Date().getTime();
        retryFn(tk.token, result, deps)
          .then((r) => {
            if (!tk.token.isCancelled) {
              setResult(r);
            }
          })
          .catch((error) => console.log(""));
      });
    }

    return () => {};
  }, [result]);

  /**
   * the resultset is not needed anymore
   */
  useEffect(() => {
    return () => {
      ct.current.unloaded = true;
      ct.current.startMs = 0;
    };
  }, []);

  /**
   * request dependency changed
   * different resultset expected
   */
  useEffect(() => {
    return () => {
      ct.current.ct?.cancel();
      ct.current.unloaded = true;
      ct.current.startMs = 0;
    };
  }, deps);

  return result;
}

/**
 * this should be in taler-utils
 * @param ms
 * @returns
 */
export async function delayMs(ms: number): Promise<void> {
  return new Promise((resolve, reject) => {
    setTimeout(() => resolve(), ms);
  });
}
